import { createAsyncThunk, createEntityAdapter, createSlice, EntityState } from '@reduxjs/toolkit'
import { useEffect, useState } from 'react'
import { ChallengeType } from '../model/model'
import { RootState, store, useAppSelector } from './store'
import { fetchUserRatings, fetchUserVideos, resetUser } from './userSlice'

const sliceName = 'challenge'

export type FetchChallengeResponseType = {
  errors: Array<string>
  result: boolean
  challenge: Array<ChallengeType>
}

type EntityStateType = {
  data: FetchChallengeResponseType['challenge']
  beltID: number
}

type SliceStateType = {
  fetching: boolean
  challenges: EntityState<EntityStateType>
  labels: Array<string>
  reviews: Record<string, FetchReviewResponseType>
}

export const getChallengeEntityID = (beltID: number) => {
  return beltID
}

export const challengeAdapter = createEntityAdapter<{ data: FetchChallengeResponseType['challenge']; beltID: number }>({
  selectId: (model) => model.beltID
})

export const challengeSelector = (beltID: number): Array<ChallengeType> | undefined => {
  const selectors = challengeAdapter.getSelectors()
  const state = store.getState().challenge.challenges

  const challenge = selectors.selectById(state, getChallengeEntityID(beltID))

  if (challenge && challenge.data) {
    return challenge.data
  }

  return undefined
}

const initialState: SliceStateType = {
  fetching: false,
  challenges: challengeAdapter.getInitialState(),
  labels: [],
  reviews: {}
}

type FetchChallengesRequestType = {
  beltID: number
  studentID?: number
  signal?: AbortSignal
}

export const fetchChallenges = createAsyncThunk<FetchChallengeResponseType, FetchChallengesRequestType>(
  `${sliceName}/fetch`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}belt/${args.beltID}/challenge`, {
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        signal: args.signal
      })
        .then((res) => res.json())
        .then((json) => {
          return json
        })

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  }
)

type AddChallengeRequestType = {
  data: Record<string, unknown>
  challengeID: number
  beltID: number
}

export type AddChallengeResponseType = {
  errors: Array<string>
  result: boolean
}

export const addChallenge = createAsyncThunk<AddChallengeResponseType, AddChallengeRequestType>(
  `${sliceName}/add`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response: AddChallengeResponseType = await fetch(`${state.setting.root}challenge/${args.challengeID}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        body: JSON.stringify(args.data)
      }).then((resp) => resp.json())

      thunkAPI.dispatch(
        fetchChallenges({
          beltID: args.beltID
        })
      )

      return response
    } catch (e) {
      return thunkAPI.rejectWithValue({})
    }
  }
)

type FetchReviewRequestType = {
  videoID: number
}

export type FetchReviewResponseType = {
  belt_id: number
  comments: string
  errors: []
  rating: { content: string; technique: string; balance: string; power: string; accuracy: string }
  accuracy: string
  balance: string
  content: string
  power: string
  technique: string
  result: boolean
  reviewer: string
  totalStars: string
  uploadVideoID: string
}

export const fetchChallengeReview = createAsyncThunk<FetchReviewResponseType, FetchReviewRequestType>(
  `${sliceName}/fetchReview`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response: FetchReviewResponseType = await fetch(`${state.setting.root}review/${args.videoID}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        }
      }).then((resp) => resp.json())

      return response
    } catch (e) {
      thunkAPI.rejectWithValue({})
    }
  }
)

export type ReviewDataType = {
  comments: string
  rating: Record<string, number>
  total: number
  totalStars: number
}

type UpdateReviewRequestType = {
  data: ReviewDataType
  videoID: string
  beltID: number
  challengeID: number
}

type UpdateReviewResponseType = {
  test: boolean
}

export const updateChallengeReview = createAsyncThunk<UpdateReviewResponseType, UpdateReviewRequestType>(
  `${sliceName}/updateReview`,
  async (args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}review/${args.videoID}`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        },
        body: JSON.stringify(args.data)
      }).then((resp) => resp.json())

      thunkAPI.dispatch(resetUser())

      thunkAPI.dispatch(
        fetchUserRatings({
          beltID: args.beltID,
          studentID: state.view_student?.studentID
        })
      )

      thunkAPI.dispatch(
        fetchUserVideos({
          beltID: args.beltID,
          challengeID: args.challengeID,
          studentID: state.view_student?.studentID
        })
      )

      return response
    } catch (e) {
      thunkAPI.rejectWithValue({})
    }
  }
)

type FetchLabelResponseType = {
  result: boolean
  errors: Array<string>
  labels: Array<string>
}

export const fetchLabels = createAsyncThunk<FetchLabelResponseType, void>(
  `${sliceName}/fetchLabels`,
  async (_args, thunkAPI) => {
    const state = (thunkAPI.getState() as RootState).config

    try {
      const response = await fetch(`${state.setting.root}labels`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': state.setting.nonce
        }
      })
        .then((resp) => resp.json())
        .then((resp) => {
          return resp as FetchLabelResponseType
        })

      return response
    } catch (e) {
      thunkAPI.rejectWithValue({})
    }
  }
)

export const challengeSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchChallenges.pending, (state) => {
      state.fetching = true
    })

    builder.addCase(fetchChallenges.fulfilled, (state, action) => {
      state.fetching = false

      challengeAdapter.upsertOne(state.challenges, {
        data: action.payload.challenge,
        beltID: action.meta.arg.beltID
      })
    })

    builder.addCase(fetchChallenges.rejected, (state) => {
      state.fetching = false
    })

    builder.addCase(fetchLabels.fulfilled, (state, action) => {
      state.labels = action.payload.labels
    })

    builder.addCase(fetchChallengeReview.fulfilled, (state, action) => {
      state.reviews[action.meta.arg.videoID] = action.payload
    })

    builder.addCase(updateChallengeReview.fulfilled, (state, action) => {
      delete state.reviews[action.meta.arg.videoID]
    })
  }
})
